import type { Notice, NoticeDataWidgetLayer, NoticeTextLayer } from './types.js';

// =============================================================================
// Station C template 7 — 저전력 모드 (시계). Figma DP1eoxbusBnlm3ekBuMUmm node 1:1022
// (2160×3840 portrait, 240px frame margin). Every value below is in that design
// space; `lowPowerClockNotice()` scales it uniformly (contain) into any canvas.
//
//   top row    weather icon │ SUN 04        (Pretendard Light 64.8px, one run so "TUE 22" keeps the same gap)
//   hour       11                            (M PLUS 1p 350px, white)
//   minute     45                            (M PLUS 1p 350px, black→#ABABAB vertical gradient)
//   seconds    59 00 [01] 02 03              (Moulpali 48px tape, slides right→left every second)
// =============================================================================

export const LOW_POWER_CLOCK_DESIGN = { width: 2160, height: 3840, fill: '#000000' } as const;
export const LOW_POWER_CLOCK_FONTS = { digits: 'M PLUS 1p', tape: 'Moulpali', text: 'Pretendard' } as const;

const white = '#FFFFFF';
const widget = (id: string, title: string, source: NoticeDataWidgetLayer['source'], box: readonly [number, number, number, number],
  extra: Partial<NoticeDataWidgetLayer> & { fontSize: number }): NoticeDataWidgetLayer => ({
  kind: 'dataWidget', id, name: title, title, source, x: box[0], y: box[1], width: box[2], height: box[3],
  z: 1, opacity: 1, rotation: 0, color: white, backgroundColor: null, align: 'center', ...extra,
});

/** Design-space layers. IDs are stable so callers (gallery, seeds) re-key them as needed. */
export function lowPowerClockLayers(): (NoticeDataWidgetLayer | NoticeTextLayer)[] {
  const digit = { fontFamily: LOW_POWER_CLOCK_FONTS.digits, fontWeight: 400, fontSize: 350 } as const;
  const clock = { kind: 'clock', mode: 'digital', hour12: false, showSeconds: false } as const;
  const divider: NoticeTextLayer = { kind: 'text', id: 'low-power-divider', name: '구분선', text: '', fontSize: 24, fontWeight: 400, color: white,
    backgroundColor: white, align: 'center', verticalAlign: 'middle', paddingX: 0, paddingY: 0,
    x: 1016, y: 1098.5, width: 2, height: 55, z: 1, opacity: 1, rotation: 0 };
  return [
    // Icon glyph 100×88 centred at (930, 1126). The widget pads ~3% inside its box, so the box
    // is 108×96 to let the icon render at its full 100px design width.
    widget('low-power-weather', '날씨 아이콘', { kind: 'weather', locationMode: 'device', latitude: 0, longitude: 0, placeName: '설치 지역' }, [876, 1078, 108, 96],
      { fontSize: 100 / 3, weatherLayout: 'horizontal', weatherFields: { place: false, icon: true, temperature: false, humidity: false, wind: false }, hideWhenUnavailable: true }),
    divider,
    // Figma has weekday + day as an auto-layout row (gap 18.9); a single "SUN 04" run keeps that
    // rhythm for narrower weekdays instead of leaving the fixed 132px box half empty.
    // Text widgets are set with 1.3 leading (size = min(fontSize, height / 1.3)), so the box is
    // 64.8 × 1.3 = 84.24 tall around the same centre (y 1126) to render the full 64.8px glyphs.
    widget('low-power-date', '요일 · 일', { kind: 'date', format: 'weekday-day' }, [1053.5, 1083.88, 480, 84.24],
      { fontSize: 64.8, fontFamily: LOW_POWER_CLOCK_FONTS.text, fontWeight: 300, align: 'left' }),
    widget('low-power-hour', '시', { ...clock, part: 'hour' }, [240, 1264, 1680, 350], digit),
    widget('low-power-minute', '분', { ...clock, part: 'minute' }, [240, 1542, 1680, 350],
      { ...digit, textGradient: { from: '#000000', to: '#ABABAB', angle: 180, start: 0.057, end: 0.46 } }),
    // Figma frame is 48px tall around 48px glyphs; the box is 72px (same centre) so ascenders are not clipped.
    widget('low-power-seconds', '초 테이프', { ...clock, part: 'secondsTape', showSeconds: true }, [240, 1926, 1680, 72],
      { fontSize: 48, fontFamily: LOW_POWER_CLOCK_FONTS.tape, fontWeight: 400, opacity: 0.8 }),
  ];
}

export type LowPowerClockDraft = Pick<Notice, 'canvasWidth' | 'canvasHeight' | 'canvasFill' | 'canvasFillOpacity' | 'durationSec' | 'layers'>;

/** Fits the portrait design into `canvasWidth × canvasHeight` (uniform scale, centred). */
export function lowPowerClockNotice(canvasWidth: number, canvasHeight: number, durationSec: number): LowPowerClockDraft {
  if (![canvasWidth, canvasHeight].every(value => Number.isFinite(value) && value > 0)) throw new Error('대지 크기를 확인해 주세요.');
  const scale = Math.min(canvasWidth / LOW_POWER_CLOCK_DESIGN.width, canvasHeight / LOW_POWER_CLOCK_DESIGN.height);
  const left = (canvasWidth - LOW_POWER_CLOCK_DESIGN.width * scale) / 2, top = (canvasHeight - LOW_POWER_CLOCK_DESIGN.height * scale) / 2;
  const layers = lowPowerClockLayers().map((layer, index) => ({
    ...layer, z: index,
    x: left + layer.x * scale, y: top + layer.y * scale, width: Math.max(1, layer.width * scale), height: Math.max(1, layer.height * scale),
    fontSize: Math.max(8, Math.min(1000, layer.fontSize * scale)),
  }));
  return { canvasWidth, canvasHeight, canvasFill: LOW_POWER_CLOCK_DESIGN.fill, canvasFillOpacity: 100, durationSec, layers };
}
